import { useState } from "react";
import { AppFooter } from "@athavita/ui-theme";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
    Building2,
    CreditCard,
    FileText,
    Key,
    LayoutDashboard,
    LogOut,
    Mail,
    Menu,
    Moon,
    Settings,
    Shield,
    Sun,
    Users,
    X
} from "lucide-react";
import { useSuiteTheme } from "@athavita/ui-theme";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";

const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/tenants", label: "Tenants", icon: Building2 },
    { to: "/invoices", label: "Invoices", icon: FileText },
    { to: "/billing", label: "Billing & Plans", icon: CreditCard },
    { to: "/resellers", label: "Resellers", icon: Users },
    { to: "/api-keys", label: "API Keys", icon: Key },
    { to: "/mail-config", label: "Mail provider", icon: Mail },
    { to: "/settings", label: "Settings", icon: Settings }
];

const pageTitles: Record<string, string> = {
    "/dashboard": "Platform Dashboard",
    "/tenants": "Tenants",
    "/invoices": "Invoices",
    "/billing": "Billing & Plans",
    "/resellers": "Resellers",
    "/api-keys": "API Keys",
    "/mail-config": "Mail provider",
    "/settings": "Settings"
};

function usePageTitle() {
    const { pathname } = useLocation();
    // Exact match first, then prefix match for detail routes
    if (pageTitles[pathname]) return pageTitles[pathname];
    if (pathname.startsWith("/tenants/")) return "Tenant Detail";
    return "Athavita Portal";
}

export function PortalShell() {
    const { logout } = useAuth();
    const { theme, toggle } = useSuiteTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const pageTitle = usePageTitle();

    const sidebar = (
        <aside
            className={cn(
                "flex w-60 shrink-0 flex-col border-r bg-muted/20 p-3",
                "h-full"
            )}
        >
            {/* Logo */}
            <div className="mb-6 flex items-center gap-2 px-2">
                <Shield className="h-6 w-6 text-primary" aria-hidden />
                <span className="text-lg font-semibold tracking-tight">Athavita</span>
            </div>

            {/* Navigation */}
            <nav className="flex flex-col gap-0.5" aria-label="Portal sections">
                {navItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                            cn(
                                "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                                isActive
                                    ? "bg-primary/10 text-primary"
                                    : "text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                            )
                        }
                    >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {item.label}
                    </NavLink>
                ))}
            </nav>

            {/* Bottom section */}
            <div className="mt-auto space-y-1 border-t pt-3">
                <button
                    type="button"
                    onClick={toggle}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                    aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                >
                    {theme === "dark"
                        ? <Sun className="h-4 w-4" />
                        : <Moon className="h-4 w-4" />}
                    {theme === "dark" ? "Light mode" : "Dark mode"}
                </button>
                <button
                    type="button"
                    onClick={logout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm text-foreground/70 hover:bg-accent hover:text-accent-foreground"
                >
                    <LogOut className="h-4 w-4" />
                    Sign out
                </button>
            </div>
        </aside>
    );

    return (
        <div className="flex h-screen bg-background text-foreground">
            {/* Desktop sidebar */}
            <div className="hidden lg:flex lg:flex-col lg:h-full">
                {sidebar}
            </div>

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 flex lg:hidden">
                    <div
                        className="fixed inset-0 bg-black/40"
                        onClick={() => setSidebarOpen(false)}
                        aria-hidden
                    />
                    <div className="relative z-50 flex h-full">
                        {sidebar}
                    </div>
                </div>
            )}

            {/* Main content area */}
            <div className="flex flex-1 flex-col overflow-hidden">
                {/* Top bar */}
                <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4">
                    <button
                        type="button"
                        className="lg:hidden p-1 rounded-md hover:bg-accent"
                        onClick={() => setSidebarOpen(true)}
                        aria-label="Open navigation"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    {sidebarOpen && (
                        <button
                            type="button"
                            className="lg:hidden p-1 rounded-md hover:bg-accent"
                            onClick={() => setSidebarOpen(false)}
                            aria-label="Close navigation"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    )}
                    <h1 className="text-base font-semibold">{pageTitle}</h1>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-auto p-6">
                    <Outlet />
                </main>
                <AppFooter />
            </div>
        </div>
    );
}
